// 短期只读媒体令牌：<img> / 下载链接 / 回放页的 ?token= 用它，不把长期登录 token 放进 URL
// 15 分钟有效，过期前 1 分钟自动刷新；换账号时作废重取
import { useEffect, useSyncExternalStore } from "react";
import { getToken, request } from "./api";

interface MediaToken {
  token: string;
  expiresAt: number;
  /** 签发时对应的登录 token（换账号即失效） */
  owner: string;
}

const REFRESH_BEFORE_MS = 60_000;

/** 是否需要（重新）取令牌：纯函数，便于测试 */
export function needsRefresh(cur: { expiresAt: number; owner: string } | null, owner: string | null, now: number): boolean {
  if (!owner) return false;
  if (!cur || cur.owner !== owner) return true;
  return cur.expiresAt - REFRESH_BEFORE_MS <= now;
}

let cur: MediaToken | null = null;
let inflight: Promise<void> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function schedule(): void {
  if (timer) clearTimeout(timer);
  if (!cur) return;
  const wait = Math.max(5_000, cur.expiresAt - REFRESH_BEFORE_MS - Date.now());
  timer = setTimeout(() => void ensureMediaToken(), wait);
}

export function ensureMediaToken(): Promise<void> {
  const owner = getToken();
  if (!owner) {
    if (cur) {
      cur = null;
      emit();
    }
    return Promise.resolve();
  }
  if (!needsRefresh(cur, owner, Date.now())) return Promise.resolve();
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await request<{ token: string; expiresAt: string }>("POST", "/api/auth/media-token");
      cur = { token: r.token, expiresAt: new Date(r.expiresAt).getTime(), owner };
      emit();
      schedule();
    } catch {
      // 失败 30 秒后再试
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void ensureMediaToken(), 30_000);
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

function snapshot(): string | null {
  return cur && cur.owner === getToken() ? cur.token : null;
}

/** 当前可用的媒体令牌（还没取到时为 null，组件此时不渲染媒体） */
export function useMediaToken(): string | null {
  const t = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    snapshot,
    () => null,
  );
  useEffect(() => {
    void ensureMediaToken();
  }, [t]);
  return t;
}
